import { Tool, type ToolProps } from '@workwave-tidal/sandbox-utils'
import { Children, isValidElement, type ReactElement, useCallback, useState } from 'react'
import type { TidalSandboxSlot } from '../index.js'

export function useTools(children?: ReactElement[] | ReactElement) {
  const tools = Children.toArray(children).reduce<ReactElement<ToolProps>[]>((acc, child) => {
    if (!isValidElement(child)) return acc

    const element = child as ReactElement<ToolProps>

    if (element.type !== Tool) {
      try {
        const elementAsSlot = element as ReactElement<TidalSandboxSlot>
        const id = elementAsSlot.props?.id ?? elementToBase64(element)
        const label = elementAsSlot.props?.label ?? ''

        acc.push(
          <Tool id={id} label={label}>
            {element}
          </Tool>,
        )
      } catch (e) {
        console.log(`The react element with type ${element.type} will be skipped.`)
        console.error('Error wrapping generic React Element to ta <Tool />', e)
      }
    }

    if (element.type === Tool) {
      acc.push(element)
    }

    return acc
  }, [])

  const firstTool = tools[0] as ReactElement<ToolProps> | undefined

  const [activeTool, setValue] = useState<string>(() => firstTool?.props.id ?? '')

  const onChangeTool = useCallback((newValue: string) => {
    setValue(newValue)
  }, [])

  return {
    activeTool,
    onChangeTool,
    tools,
  }
}

// ATTENTION: Trick directly from MDN docs on how to convert a string with unknown characters to a base64 string
// See: https://developer.mozilla.org/en-US/docs/Web/API/Window/btoa#unicode_strings
function elementToBase64(element: ReactElement) {
  const elementString = element.toString()
  const bytes = new TextEncoder().encode(elementString)
  const binString = Array.from(bytes, byte => String.fromCodePoint(byte)).join('')
  return btoa(binString)
}
